import { useCallback, useState } from 'react';
import { clamp } from './chartMath';

/**
 * Pointer/keyboard hover-index tracking for charts whose data sits at N
 * evenly-spaced slots along x (bars, candles) — the same interaction model
 * LineChart uses for its tooltip, generalized so each chart doesn't
 * reimplement the pointer-to-index math and arrow-key navigation.
 *
 * @param {{ n: number, padLeft: number, plotWidth: number, w: number }} args
 */
export function useIndexHover({ n, padLeft, plotWidth, w }) {
  const [hoverIndex, setHoverIndex] = useState(null);

  const handlePointerMove = useCallback(
    (event) => {
      if (n === 0) return;
      const rect = event.currentTarget.getBoundingClientRect();
      if (!rect.width) return;
      const xView = (event.clientX - rect.left) * (w / rect.width) - padLeft;
      const ratio = plotWidth > 0 ? xView / plotWidth : 0;
      setHoverIndex(clamp(Math.round(ratio * (n - 1)), 0, n - 1));
    },
    [n, w, padLeft, plotWidth]
  );

  const handlePointerLeave = useCallback(() => setHoverIndex(null), []);

  const handleKeyDown = useCallback(
    (event) => {
      if (n === 0) return;
      const base = hoverIndex ?? 0;
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        setHoverIndex(clamp(base - 1, 0, n - 1));
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        setHoverIndex(clamp(base + 1, 0, n - 1));
      } else if (event.key === 'Home') {
        setHoverIndex(0);
      } else if (event.key === 'End') {
        setHoverIndex(n - 1);
      } else if (event.key === 'Escape') {
        setHoverIndex(null);
      }
    },
    [n, hoverIndex]
  );

  return { hoverIndex, handlePointerMove, handlePointerLeave, handleKeyDown };
}

/**
 * Clamped `left`/`top` style for a tooltip pill anchored above point
 * `(x, y)`. `x`/`y`/`padLeft`/`padRight`/`w` are in the chart's frozen
 * design-space (SVG viewBox) units; `scale` (rendered width / design
 * width, from `useChartSize`) converts them into real on-screen pixels,
 * since the tooltip is a plain HTML overlay rather than part of the
 * scaled SVG. `tooltipWidth` is already a real pixel measurement
 * (`offsetWidth`), so it's used as-is.
 */
export function tooltipStyleAt(x, y, tooltipWidth, padLeft, padRight, w, scale = 1) {
  const half = tooltipWidth / 2;
  const left = clamp(x * scale, padLeft * scale + half, w * scale - padRight * scale - half);
  return {
    left,
    top: y * scale,
    transform: 'translate(-50%, -100%)',
    // Hide until measured so the pill doesn't render unclamped for one frame.
    visibility: tooltipWidth > 0 ? 'visible' : 'hidden',
  };
}
